import { useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { FinanceRate } from '@kanda-libs/ks-frontend-services';

import { formatToCurrency, formatToPercentage } from 'utils';
import {
  calculateMonthlyRepaymentRaw,
  checkRateIsValid,
} from 'utils/FinanceRate-functions';

import { FinanceFormValues, useFinanceFormContext } from '../../context';

export interface RepaymentInfoHook {
  rate: FinanceRate | undefined;
  title: string;
  showRepaymentInfo: boolean;
  subtitle: string | undefined;
  monthlyPayment: string;
  showDepositPayment: boolean;
  depositTitle: string;
  depositPayment: string | undefined;
  amountToFinance: string;
  totalInterest: string;
  totalRepayment: string;
  disabled: boolean;
  isSubmitting: boolean;
}

export default function useRepaymentInfo(): RepaymentInfoHook {
  const { job, allRates, isSubmitting } = useFinanceFormContext();
  const { setValue } = useFormContext<FinanceFormValues>();
  const [
    apr,
    aprType,
    duration,
    deferredDuration,
    deposit,
    depositPercent,
    commission,
  ] = useWatch<
    FinanceFormValues,
    [
      'apr',
      'aprType',
      'duration',
      'deferredDuration',
      'deposit',
      'depositPercent',
      'commission',
    ]
  >({
    name: [
      'apr',
      'aprType',
      'duration',
      'deferredDuration',
      'deposit',
      'depositPercent',
      'commission',
    ],
  });

  const total = useMemo(() => job.total?.amount_vat_inclusive?.amount, [job]);

  const rate = useMemo(() => {
    if (duration === undefined || total === undefined) return undefined;
    const aprKey = `${apr}-${aprType}`;
    const durationKey = `${
      deferredDuration ? `${deferredDuration}-` : ''
    }${duration}`;
    const allDurationRates = allRates[aprKey][durationKey];
    if (!allDurationRates || allDurationRates.length === 0) return undefined;
    if (allDurationRates.length === 1) return allDurationRates[0];
    const validRates = allDurationRates.filter((currentRate) =>
      checkRateIsValid(currentRate, total, deposit ?? 0),
    );
    if (!validRates || validRates.length === 0) return undefined;
    return validRates[0];
  }, [allRates, apr, aprType, deferredDuration, duration, deposit, total]);

  const showRepaymentInfo = useMemo(
    () => Boolean(apr) && Boolean(duration),
    [apr, duration],
  );

  const title = useMemo(() => {
    if (aprType === 'BUYNOW_PAYLATER') return 'Buy Now, Pay Later';
    if (aprType === 'INTEREST_FREE') return 'Interest Free Finance';
    return `Interest Bearing Finance, ${formatToPercentage(
      parseInt(apr, 10),
    )} APR`;
  }, [aprType, apr]);

  const subtitle = useMemo(() => {
    if (!duration) return undefined;
    if (aprType === 'BUYNOW_PAYLATER') {
      if (!deferredDuration)
        return 'Interest free term followed by monthly interest bearing repayments';
      return `Pay nothing, some or all of your loan at 0% for ${deferredDuration} months, followed by ${duration} monthly repayments at ${formatToPercentage(
        parseInt(apr, 10),
      )} APR representative`;
    }
    if (aprType === 'INTEREST_FREE')
      return `${duration} monthly repayments at no extra cost to you!`;
    return `${duration} monthly repayments at ${formatToPercentage(
      parseInt(apr, 10),
    )} APR representative`;
  }, [duration, aprType, deferredDuration, apr]);

  const depositAmount = useMemo(() => deposit ?? 0, [deposit]);

  const monthly = useMemo(() => {
    if (total === undefined || !rate) return 0;
    return calculateMonthlyRepaymentRaw(
      total - depositAmount,
      rate.apr ?? 0,
      rate?.duration ?? 1,
    );
  }, [depositAmount, total, rate]);

  const monthlyPayment = useMemo(() => formatToCurrency(monthly), [monthly]);

  const showDepositPayment = useMemo(
    () => job.deposit_type === 'no_deposit',
    [job],
  );

  const depositTitle = useMemo(() => {
    if (deposit === undefined || depositPercent === undefined)
      return 'Deposit (to pay today)';
    if (deposit === 0) return 'Deposit';
    if (depositPercent === 'custom') return 'Deposit (to pay today)';
    return `Deposit (${depositPercent}, to pay today)`;
  }, [deposit, depositPercent]);

  const depositPayment = useMemo(
    () => formatToCurrency(depositAmount),
    [depositAmount],
  );

  const loan = useMemo(() => {
    if (!total) return 0;
    return total - depositAmount;
  }, [total, depositAmount]);

  const amountToFinance = useMemo(() => formatToCurrency(loan), [loan]);

  const totalRepayment = useMemo(() => {
    if (duration === undefined) return 0;
    return Math.ceil(monthly * duration);
  }, [duration, monthly]);

  const totalInterest = useMemo(() => {
    if (!loan || aprType === 'INTEREST_FREE') return 0;
    return totalRepayment - loan;
  }, [loan, totalRepayment, aprType]);

  const totalPayableDisplay = useMemo(() => {
    if (showDepositPayment)
      return formatToCurrency(totalRepayment + depositAmount);
    return formatToCurrency(totalRepayment);
  }, [showDepositPayment, totalRepayment, depositAmount]);

  const disabled = useMemo(() => !commission?.value, [commission]);

  useEffect(() => {
    setValue('rate', rate);
  }, [rate, setValue]);

  return {
    rate,
    title,
    showRepaymentInfo,
    subtitle,
    monthlyPayment,
    showDepositPayment,
    depositTitle,
    depositPayment,
    amountToFinance,
    totalInterest: formatToCurrency(totalInterest),
    totalRepayment: totalPayableDisplay,
    disabled,
    isSubmitting,
  };
}
